import * as React from 'react';
import NextImage from 'next/image';
import { getCourtPageUrl } from 'constants/pages';
import type { EngagementSource } from 'constants/payloads/club/engagementBeacon';
import { getDefaultVenueImageBySport } from 'constants/venues';
import { SportsEnum, VenueAccessTypesEnum, VenueNetsEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { getAccessParamters } from 'utils/shared/venues';
import { useEngagementImpression } from 'hooks/useEngagementImpression';
import CourtFlat from 'svg/CourtFlat';
import Location from 'svg/Location';
import Net from 'svg/Net';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

interface Props {
  id: string;
  slug: string;
  imageUrl?: string;
  title: string;
  addressString: string;
  accessType?: VenueAccessTypesEnum | null;
  totalCourtCount?: number;
  pickleballNets?: VenueNetsEnum | null;
  primarySport?: SportsEnum | null;
  isLink?: boolean;
  /** Persistent ring for pickers where the card is a choice rather than a link. */
  isSelected?: boolean;
  /** @deprecated no-op; the card now always uses the compact home layout. */
  homePage?: boolean;
  /** When set, fires a CLUB_PAGE impression with this source (opt-in for discovery surfaces). */
  impressionSource?: EngagementSource;
}

const Item = ({
  name,
  Icon,
}: {
  name: string | number;
  Icon: ({ className }: React.SVGProps<SVGSVGElement>) => JSX.Element;
}) => {
  return (
    <div className="flex items-center gap-0.5 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:gap-ds-xs">
      <Icon className="h-3.5 w-3.5 shrink-0 text-color-bg-lightmode-icon dark:text-color-bg-lightmode-icon lg:h-4 lg:w-4" />
      <span className="typography-product-text-card whitespace-nowrap">{name}</span>
    </div>
  );
};

const Content = ({
  imageUrl,
  title,
  addressString,
  accessType,
  totalCourtCount,
  pickleballNets,
  primarySport,
  isSelected,
  containerRef,
}: Props & { containerRef?: React.Ref<HTMLDivElement> }) => {
  const accessParameters = getAccessParamters(accessType);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'flex h-full w-full flex-col gap-ds-sm rounded-lg transition-shadow',
        isSelected
          ? 'bg-color-bg-lightmode-primary shadow-[0_6px_30px_rgba(0,0,0,0.24)] ring-1 ring-color-border-hover-lightmode ring-offset-8 ring-offset-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary dark:ring-color-border-hover-darkmode dark:ring-offset-color-bg-darkmode-primary'
          : 'ring-color-bg-lightmode-invert ring-offset-color-bg-darkmode-invert dark:ring-color-bg-darkmode-invert dark:ring-offset-color-bg-lightmode-invert lg:hover:ring-2 lg:hover:ring-offset-8',
      )}
    >
      <NextImage
        src={imageUrl || getDefaultVenueImageBySport(primarySport)}
        alt={title}
        className="aspect-[343/180] w-full rounded-lg object-cover"
        loading="lazy"
        height={180}
        width={343}
      />
      <div className="flex flex-col gap-ds-sm">
        <div className="flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          <Location className="h-3.5 w-3.5 shrink-0 text-color-bg-lightmode-icon dark:text-color-bg-darkmode-icon lg:h-4 lg:w-4" />
          <span className="typography-product-text-card truncate">{addressString}</span>
        </div>
        <div className="typography-product-card-title text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {title}
        </div>
        <div className="flex flex-wrap gap-x-ds-md gap-y-ds-sm">
          {!!accessParameters && <Item {...accessParameters} />}
          {!!totalCourtCount && (
            <Item
              name={pluralize({
                count: totalCourtCount,
                singular: 'Court',
                plural: 'Courts',
              })}
              Icon={CourtFlat}
            />
          )}
          {!!pickleballNets && (
            <Item
              name={
                pickleballNets === VenueNetsEnum.Permanent ||
                pickleballNets === VenueNetsEnum.Tennis
                  ? 'Permanent nets'
                  : 'Non-permanent nets'
              }
              Icon={Net}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default function CardVenue({
  isLink = true,
  impressionSource,
  homePage: _homePage,
  ...props
}: Props) {
  const cardRef = React.useRef<HTMLDivElement>(null);
  useEngagementImpression(cardRef, {
    entityType: 'CLUB_PAGE',
    entityId: props.id,
    source: impressionSource ?? 'BROWSE',
    disabled: !impressionSource,
  });

  if (isLink) {
    return (
      <Link href={getCourtPageUrl(props.slug)} className="w-full">
        <Content containerRef={cardRef} {...props} />
      </Link>
    );
  }

  return <Content containerRef={cardRef} {...props} />;
}
